
import { Component, Prop, Vue } from 'bits/vue'
import OzBaseButton, { OzBaseButtonHrefMode } from 'library/v4/components/OzBaseButton.vue'

export enum OzPlainButtonColorScheme {
  Bare = 'Bare',
  Primary = 'Primary',
  Secondary = 'Secondary',
  SecondaryIcon = 'SecondaryIcon',
  TertiaryIcon = 'TertiaryIcon',
  Active = 'Active',
  SecondaryIconInverse = 'SecondaryIconInverse',
}

export enum OzPlainButtonSizePreset {
  Bare = 'Bare',
  H44px = 'H44px',
  H40px = 'H40px',
  H32px = 'H32px',
  H28px = 'H28px',
  H24px = 'H24px',
}

/**
 * Text-only button.
 */

@Component({ components: { OzBaseButton } })
export default class OzPlainButton extends Vue {
  ColorScheme = OzPlainButtonColorScheme
  SizePreset = OzPlainButtonSizePreset

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Whether the button is disabled.
   */
  @Prop({ default: false }) disabled!: boolean

  /**
   * Automatically uses an `<a>` tag if href is present, `<button>` otherwise.
   */
  @Prop({ default: undefined }) href?: string
  @Prop({ default: undefined }) hrefTarget?: string

  /**
   * See `OzBaseButton`.
   */
  @Prop({ default: OzBaseButtonHrefMode.Default }) hrefMode?: OzBaseButtonHrefMode

  /**
   * Determines the color of the link. Use `Active` when you want to force the selection color.
   */
  @Prop({ default: OzPlainButtonColorScheme.Primary }) colorScheme!: OzPlainButtonColorScheme

  /**
   * Applies preset sizes/shapes.
   * They are named with pixel values for developer ergonomics -- those are the values you see in the mockups.
   * Technically, the pixel value could vary depending on the size of `rem`.
   */
  @Prop({ default: OzPlainButtonSizePreset.H44px }) sizePreset!: OzPlainButtonSizePreset

  /**
   * Defines whether the button text should be truncated or not.
   * Use case:
   *
   * 1. `true` - truncate the button text if it exceeds the button's width
   * 2. `false` - do not truncate the button text, allowing it to stretch the button's height
   */
  @Prop({ default: true }) shouldTruncateText!: boolean

  /**
   * Defines whether the button's height should be fixed or adjusted by its content.
   */
  @Prop({ default: false }) autoHeight!: boolean

  /**
   * Text. Generates `title`, which is useful when text is truncated.
   */
  @Prop({ default: undefined }) text?: string

  /**
   * Title. If not defined, text will be used instead.
   */
  @Prop({ required: false }) title?: string
}

export { OzBaseButtonHrefMode as OzPlainButtonHrefMode }
